import { CSSProperties, FC, useMemo } from "react";
import { Chart } from "../../../components/charts/Chart";
import { LineChart } from "../../../components/charts/LineChart";
import { getTeamColors } from "components/colors";
import { ensurePair, extractFirstNonZero, parseGPP } from "./helper";
import { Text } from "components/common/Text";

type IHStatus = {
  sts?: string;
  c?: Record<string, any>;
};

export const Stats: FC<{ status: IHStatus }> = ({ status }) => {
  const { home, away } = getTeamColors(status as any);

  const data = useMemo(() => {
    if (!status || typeof status !== "object") {
      return {} as any;
    }

    const splitStatus = String(status.sts || "")
      .split("|")
      .filter(Boolean);

    const penalties = extractFirstNonZero(splitStatus, [
      "Penalties",
      "PENALTIES",
    ]);

    const goalsOnPP = extractFirstNonZero(splitStatus, [
      "Goals on Power Play",
      "GOALS ON POWER PLAY",
      "Power Play Goals",
      "POWER PLAY GOALS",
      "PPG",
    ]);

    const gpp = parseGPP(splitStatus);

    const periods: { key: string; v: [number, number] }[] = [];
    const c = (status as any).c || {};

    (["P1", "P2", "P3", "OT", "SO"] as const).forEach((k) => {
      const v = c?.[k];
      if (v) periods.push({ key: k, v: ensurePair(v) });
    });

    if (c?.T) periods.push({ key: "T", v: ensurePair(c.T) });

    return {
      penalties,
      goalsOnPP,
      gpp,
      periods,
    } as const;
  }, [status]);

  const ppEffHome = data.gpp?.efficiency?.home ?? 0;
  const ppEffAway = data.gpp?.efficiency?.away ?? 0;
  const cssVariables = useMemo(() => {
    const vars: CSSProperties = {};
    (vars as Record<string, string>)["--home-color"] = home;
    (vars as Record<string, string>)["--away-color"] = away;
    return vars;
  }, [home, away]);
  const periodGridColumns = "repeat(auto-fit, minmax(14vw, 1fr))";

  return (
    <div
      className="flex w-full flex-col items-center justify-start gap-[2vw]"
      style={cssVariables}
    >
      <div className="flex w-full flex-row items-stretch justify-between gap-[2vw]">
        <Chart
          homeValue={data.penalties ? data.penalties[0] : 0}
          awayValue={data.penalties ? data.penalties[1] : 0}
          title="PENALTIES"
        />
        <Chart
          homeValue={Number(ppEffHome.toFixed(1))}
          awayValue={Number(ppEffAway.toFixed(1))}
          title="POWER PLAY GOALS (%)"
        />
        <Chart
          homeValue={ppEffHome}
          awayValue={ppEffAway}
          title="PP EFFICIENCY (%)"
        />
      </div>

      {data.periods && data.periods.length > 0 && (
        <div
          className="grid w-full gap-[1.5vw]"
          style={{ gridTemplateColumns: periodGridColumns }}
        >
          {data.periods.map(
            ({ key, v }: { key: string; v: [number, number] }) => (
              <div
                key={key}
                className={`flex flex-col gap-[1vw] rounded-[0.5vw] border-2 border-border bg-darkestBlue p-[1vw]`}
              >
                <div className={`font-bold text-statisticTitle opacity-90`}>
                  <Text variant="chartTitle">{key}</Text>
                </div>
                <div className="grid grid-cols-2 gap-[1vw]">
                  <div className="rounded-[0.5vw] border-b-[1vw] border-b-[var(--home-color)] bg-white p-[1vw] text-center font-extrabold text-black">
                    <Text variant="chartValue">{v[0]}</Text>
                  </div>
                  <div className="rounded-[0.5vw] border-b-[1vw] border-b-[var(--away-color)] bg-white p-[1vw] text-center font-extrabold text-black">
                    <Text variant="chartValue">{v[1]}</Text>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}

      <div className="flex w-full flex-col gap-[2vw]">
        {data.penalties &&
          (data.penalties[0] !== 0 || data.penalties[1] !== 0) && (
            <LineChart
              homeValue={data.penalties[0]}
              awayValue={data.penalties[1]}
              title="PENALTIES (CUMULATIVE)"
            />
          )}
        {data.gpp && (ppEffHome !== 0 || ppEffAway !== 0) && (
          <LineChart
            homeValue={Number(ppEffHome.toFixed(1))}
            awayValue={Number(ppEffAway.toFixed(1))}
            title="POWER PLAY GOALS (%)"
          />
        )}
        {data.gpp && (ppEffHome !== 0 || ppEffAway !== 0) && (
          <LineChart
            homeValue={Number(ppEffHome.toFixed(1))}
            awayValue={Number(ppEffAway.toFixed(1))}
            title="PP EFFICIENCY (%)"
          />
        )}
      </div>
    </div>
  );
};
